import type {
    ObjectListField,
    PreferenceSettingDefinition,
    SettingDefinition,
    SettingError,
    SettingListValue,
    SettingRecord,
    SettingsCard,
    SettingsCategory,
    SettingsContext,
    SettingsRule,
    SettingsSearchResult,
    SettingsSubcategory,
    SettingValue
} from '../models/settings-hub.model';

/** The addresses that the legacy page accepts: the check that AngularJS applies to a field of type email */
const EMAIL_PATTERN = /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;

/** `haystack` is the text of the setting that the search looks into, `cardTitle` the one of its card; both are normalized */
export type SettingsSearchEntry = SettingsSearchResult & { haystack: string; cardTitle: string };

/** A subcategory found by its title (or by the one of its category), to be reached with a button */
export type SettingsSectionMatch = { category: SettingsCategory; subcategory: SettingsSubcategory };

/** Where a card found by the search comes from */
export type SettingsViewCardOrigin = { subcategoryId: string; subcategoryTitle: string; categoryTitle: string };

/** `groups` are the same settings, with the ones of a pair together, ready to be rendered */
export type SettingsViewCard = { id: string; title: string; fullWidth?: boolean; settings: SettingDefinition[]; groups: SettingsGroup[]; origin?: SettingsViewCardOrigin };

/** The search starts with this number of characters: a shorter text matches too many settings to be of any use */
export const MIN_SEARCH_LENGTH = 3;

export function isSearchable(query: string): boolean {
    return query.trim().length >= MIN_SEARCH_LENGTH;
}

export function flattenSettings(categories: SettingsCategory[]): SettingsSearchResult[] {
    return categories.flatMap(category =>
        category.subcategories.flatMap(subcategory =>
            subcategory.cards.flatMap(card =>
                card.settings.map(setting => ({ category, subcategory, card, setting }))
            )
        )
    );
}

/** The preferences that each subcategory edits, by subcategory id: used to tell which sections have changes not saved */
export function getPreferenceKeysBySubcategory(categories: SettingsCategory[]): Map<string, Set<string>> {
    const keysBySubcategory = new Map<string, Set<string>>();

    for (const { subcategory, setting } of flattenSettings(categories)) {
        if (isPreferenceSetting(setting)) {
            const keys = keysBySubcategory.get(subcategory.id) ?? new Set<string>();

            keys.add(setting.preferenceKey);
            keysBySubcategory.set(subcategory.id, keys);
        }
    }

    return keysBySubcategory;
}

/** Where a setting is in the tree of the settings */
export function findSettingLocation(categories: SettingsCategory[], settingId: string): SettingsSearchResult | undefined {
    return flattenSettings(categories).find(({ setting }) => setting.id === settingId);
}

/** The path of a setting in the tree, e.g. "Vendita › Cassa e Comande › Comande › Ristampa all'invio". */
export function getSettingPath(
    origin: SettingsViewCardOrigin,
    card: Pick<SettingsViewCard, 'title'>,
    setting: SettingDefinition,
    translate: (key: string, params?: Record<string, string | number>) => string
): string {
    return [
        translate(origin.categoryTitle),
        translate(origin.subcategoryTitle),
        translate(card.title),
        translate(setting.label, setting.labelParams)
    ].join(' › ');
}

export function isPreferenceSetting(setting: SettingDefinition): setting is PreferenceSettingDefinition {
    return setting.control !== 'action' && setting.control !== 'component' && setting.control !== 'device-toggle';
}

function evaluateRule(rule: boolean | SettingsRule | undefined, context: SettingsContext): boolean {
    return typeof rule === 'function' ? rule(context) : !!rule;
}

/** A card is visible when at least one of its settings is visible. */
export function isCardVisible(card: SettingsCard, context: SettingsContext): boolean {
    return card.settings.some(setting => isSettingVisible(setting, context));
}

/** A subcategory is visible when at least one of its cards is visible. */
export function isSubcategoryVisible(subcategory: SettingsSubcategory, context: SettingsContext): boolean {
    return subcategory.cards.some(card => isCardVisible(card, context));
}

/** Categories with only the subcategories that are currently visible; the categories left empty are dropped. */
export function getVisibleCategories(categories: SettingsCategory[], context: SettingsContext): SettingsCategory[] {
    return categories
        .map(category => ({ ...category, subcategories: category.subcategories.filter(subcategory => isSubcategoryVisible(subcategory, context)) }))
        .filter(category => category.subcategories.length > 0);
}

export function isSettingVisible(setting: SettingDefinition, context: SettingsContext): boolean {
    return !setting.visibleWhen || setting.visibleWhen(context);
}

export function isSettingReadonly(setting: SettingDefinition, context: SettingsContext): boolean {
    return isPreferenceSetting(setting) && evaluateRule(setting.readonly, context);
}

/** A setting that cannot be edited by the user, either because it is read only or because it is disabled by a rule. */
export function isSettingDisabled(setting: SettingDefinition, context: SettingsContext): boolean {
    return isSettingReadonly(setting, context) || evaluateRule(setting.disabledWhen, context);
}

/** Converts the value stored in the preferences (strings, 'on'/'off' already parsed to booleans) to the value handled by the control. */
export function normalizeSettingValue(setting: PreferenceSettingDefinition, rawValue: unknown): SettingValue {
    const value = rawValue === undefined || rawValue === null ? setting.defaultValue ?? null : rawValue;

    switch (setting.control) {
        case 'toggle':
            return setting.onValue !== undefined
                ? value === setting.onValue
                : value === true || value === 'true' || value === 'on' || value === 1 || value === '1';
        case 'number':
        case 'item-picker':
            return toNumberOrNull(value);
        case 'select':
            return setting.numeric ? toNumberOrNull(value) : value === null ? '' : String(value);
        case 'multiselect':
        case 'chips':
            return parseList(setting, value);
        case 'object-list':
            return parseObjectList(value);
        case 'indexed-texts':
            return parseIndexedTexts(value);
        default:
            return value === null ? '' : String(value);
    }
}

/** Reads the texts of a numbered series from the saved JSON object (number as key); the series is as long as its highest number. */
export function parseIndexedTexts(value: unknown): SettingListValue {
    if (Array.isArray(value)) {
        return value.map(text => String(text ?? ''));
    }

    try {
        const texts = JSON.parse(String(value ?? ''));

        if (!isRecord(texts)) {
            return [];
        }

        const count = Math.max(0, ...Object.keys(texts).map(Number).filter(Number.isInteger));

        return Array.from({ length: count }, (_, index) => String(texts[index + 1] ?? ''));
    } catch (error) {
        return [];
    }
}

function isRecord(item: unknown): item is SettingRecord {
    return typeof item === 'object' && item !== null && !Array.isArray(item);
}

function parseObjectList(value: unknown): SettingRecord[] {
    if (Array.isArray(value)) {
        return value.filter(isRecord);
    }

    if (typeof value !== 'string' || !value) {
        return [];
    }

    try {
        const parsedValue = JSON.parse(value);
        return Array.isArray(parsedValue) ? parsedValue.filter(isRecord) : [];
    } catch (error) {
        return [];
    }
}

/** The items of a list of objects, whatever the value is */
const asRecords = (value: unknown): SettingRecord[] => Array.isArray(value) ? value.filter(isRecord) : [];

const isBlank = (value: unknown) => value === null || value === undefined || value === '';

/** An item where the user did not fill anything */
export function isEmptyRecord(item: SettingRecord) {
    return Object.values(item).every(value => isBlank(value) || value === false);
}

/** Converts a value edited by the user, that can be empty, to the kind of value handled by the setting. */
export function coerceSettingValue(setting: PreferenceSettingDefinition, value: SettingValue): SettingValue {
    // A toggle is already a boolean, whatever value it saves when it is on
    if (setting.control === 'toggle' && typeof value === 'boolean') {
        return value;
    }

    return normalizeSettingValue({ ...setting, defaultValue: undefined }, value);
}

function toNumberOrNull(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? null : parsedValue;
}

function parseList(setting: PreferenceSettingDefinition, value: unknown): SettingListValue {
    if (Array.isArray(value)) {
        return value;
    }

    if (value === null || value === '') {
        return [];
    }

    const items = (() => {
        if (setting.storage === 'lines') {
            return String(value).split('\n');
        }

        try {
            const parsedValue = JSON.parse(String(value));
            return Array.isArray(parsedValue) ? parsedValue : [];
        } catch (error) {
            return [];
        }
    })();

    return setting.control === 'multiselect' && setting.numeric ? items.map(Number) : items;
}

/**
 * The texts of a numbered series after the number of the texts changed: each text is the current one or,
 * when there is none, the one that was saved (so the texts of the exits that were removed come back if the exits are added again).
 */
export function resizeIndexedTexts(currentTexts: SettingValue | undefined, savedTexts: SettingListValue, count: number): SettingListValue {
    const texts = Array.isArray(currentTexts) ? currentTexts as SettingListValue : [];

    return Array.from({ length: count }, (_, index) => texts[index] || savedTexts[index] || '');
}

/**
 * The list with the item typed by the user added; the list is returned as is when the item is empty, not valid,
 * already in the list or when the list is full.
 */
export function addListItem(list: SettingListValue, text: string, options: { maxItems?: number; filter?: (text: string) => string | number | null }): SettingListValue {
    const trimmedText = text.trim();
    const item = options.filter ? options.filter(trimmedText) : trimmedText;

    if (item === null || !trimmedText || Number.isNaN(item) || list.includes(item) || (options.maxItems !== undefined && list.length >= options.maxItems)) {
        return list;
    }

    return [...list, item];
}

/** The filter that the legacy page applies to the percentages typed for a chip: a number above 0 and below 101, cut to an integer */
export function legacyPercentageFilter(text: string): number | null {
    const value = text.trim();

    return +value === +value && +value > 0 && +value < 101 ? Math.trunc(+value) : null;
}

/** Converts the value handled by the control to the value saved in the preference. */
export function serializeSettingValue(setting: PreferenceSettingDefinition, value: SettingValue): string | number | boolean {
    if (setting.control === 'toggle' && setting.onValue !== undefined) {
        return value === true ? setting.onValue : '';
    }

    if (setting.control === 'object-list') {
        return JSON.stringify(asRecords(value).filter(item => !isEmptyRecord(item)));
    }

    if (setting.control === 'indexed-texts') {
        const texts = Array.isArray(value) ? value : [];

        return texts.length ? JSON.stringify(Object.fromEntries(texts.map((text, index) => [index + 1, text || null]))) : '';
    }

    if (!Array.isArray(value)) {
        return value ?? '';
    }

    if (!value.length) {
        return '';
    }

    return setting.storage === 'lines' ? value.join('\n') : JSON.stringify(value);
}

export function areSettingValuesEqual(valueA: SettingValue | undefined, valueB: SettingValue | undefined): boolean {
    if (Array.isArray(valueA) && Array.isArray(valueB)) {
        return valueA.length === valueB.length
            && valueA.every((item, index) => item === valueB[index] || (isRecord(item) && JSON.stringify(item) === JSON.stringify(valueB[index])));
    }

    return valueA === valueB;
}

/**
 * Tells if a setting has the same value once saved: the rows of a list that are still empty are not saved, so adding one
 * is not a change.
 */
export function areSettingValuesEquivalent(setting: PreferenceSettingDefinition, valueA: SettingValue | undefined, valueB: SettingValue | undefined): boolean {
    if (setting.control === 'object-list') {
        return areSettingValuesEqual(asRecords(valueA).filter(item => !isEmptyRecord(item)), asRecords(valueB).filter(item => !isEmptyRecord(item)));
    }

    return areSettingValuesEqual(valueA, valueB);
}

/** The value of a setting that has been cleared. */
export function emptySettingValue(setting: PreferenceSettingDefinition): SettingValue {
    switch (setting.control) {
        case 'toggle':
            return false;
        case 'number':
        case 'item-picker':
            return null;
        case 'select':
            return setting.numeric ? null : '';
        case 'multiselect':
        case 'chips':
        case 'object-list':
        case 'indexed-texts':
            return [];
        default:
            return '';
    }
}

export function getSettingError(setting: SettingDefinition, value: SettingValue, context: SettingsContext): SettingError | null {
    if (!isPreferenceSetting(setting) || setting.control === 'toggle') {
        return null;
    }

    if (setting.control === 'object-list') {
        return getObjectListError(setting, asRecords(value), context);
    }

    if (Array.isArray(value)) {
        if (!value.length) {
            return evaluateRule(setting.required, context) ? 'required' : null;
        }

        return setting.maxItems !== undefined && value.length > setting.maxItems ? 'maxItems' : null;
    }

    if (value === null || value === '') {
        return evaluateRule(setting.required, context) ? 'required' : null;
    }

    if (typeof value === 'number') {
        return (setting.min !== undefined && value < setting.min) || (setting.max !== undefined && value > setting.max) ? 'range' : null;
    }

    if (typeof value === 'string') {
        if (setting.control === 'email' && !EMAIL_PATTERN.test(value)) {
            return 'pattern';
        }

        if (setting.maxLength !== undefined && value.length > setting.maxLength) {
            return 'maxLength';
        }

        if (setting.pattern && !setting.pattern.test(value)) {
            return 'pattern';
        }
    }

    return null;
}

/** The items that have been started must have all the required fields and valid values. */
function getObjectListError(setting: PreferenceSettingDefinition, items: SettingRecord[], context: SettingsContext): SettingError | null {
    const startedItems = items.filter(item => !isEmptyRecord(item));

    if (!startedItems.length) {
        return evaluateRule(setting.required, context) ? 'required' : null;
    }

    for (const item of startedItems) {
        for (const field of setting.objectList?.fields ?? []) {
            const error = getObjectListFieldError(field, item);

            if (error) {
                return error;
            }
        }
    }

    return null;
}

/** The error of a field of a row of a list. A row where nothing has been filled is not saved, so it has no errors. */
export function getObjectListFieldError(field: ObjectListField, item: SettingRecord): Extract<SettingError, 'required' | 'pattern'> | null {
    if (isEmptyRecord(item)) {
        return null;
    }

    const value = item[field.key];

    if (field.required && isBlank(value)) {
        return 'required';
    }

    return field.pattern && typeof value === 'string' && value && !field.pattern.test(value) ? 'pattern' : null;
}

export function normalizeSearchText(text: string): string {
    return text
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .trim();
}

export function buildSearchIndex(results: SettingsSearchResult[], translate: (key: string, params?: Record<string, string | number>) => string): SettingsSearchEntry[] {
    return results.map(result => {
        const { card, setting } = result;
        const texts = [
            translate(setting.label, setting.labelParams),
            setting.description ? translate(setting.description) : '',
            ...(setting.keywords ?? [])
        ];

        return { ...result, haystack: normalizeSearchText(texts.join(' ')), cardTitle: normalizeSearchText(translate(card.title)) };
    });
}

/**
 * A setting is found when every word of the query is contained in its label, description or keywords, in any order.
 * When the query is the title of a card, all the settings of the card are found.
 * The sections found by their title are not settings: see `searchSections`.
 */
export function searchSettings(index: SettingsSearchEntry[], query: string): SettingsSearchResult[] {
    const normalizedQuery = normalizeSearchText(query);
    const words = normalizedQuery.split(/\s+/).filter(Boolean);

    if (!words.length) {
        return [];
    }

    return index
        .filter(entry => entry.cardTitle === normalizedQuery || words.every(word => entry.haystack.includes(word)))
        .map(({ category, subcategory, card, setting }) => ({ category, subcategory, card, setting }));
}

/**
 * The subcategories that the query names: the one whose title is the query or, if the query is the title of a category,
 * all the subcategories of the category. The user reaches them with a button instead of seeing all their settings.
 * A subcategory with a card titled like the query is left out: the card shows all its settings, and reaches the subcategory.
 */
export function searchSections(
    categories: SettingsCategory[],
    query: string,
    translate: (key: string) => string
): SettingsSectionMatch[] {
    const normalizedQuery = normalizeSearchText(query);

    if (!normalizedQuery) {
        return [];
    }

    return categories.flatMap(category => {
        const categoryFound = normalizeSearchText(translate(category.title)) === normalizedQuery;

        return category.subcategories
            .filter(subcategory => categoryFound || (
                normalizeSearchText(translate(subcategory.title)) === normalizedQuery
                // The section with a card of the same title (the legacy page has many) is already shown by that card, whole
                && !subcategory.cards.some(card => normalizeSearchText(translate(card.title)) === normalizedQuery)
            ))
            .map(subcategory => ({ category, subcategory }));
    });
}

export type SettingsGroup = {
    settings: SettingDefinition[];
    /** A divider goes under the group: it is not the last one, and neither it nor the next one is a pair (a pair has its own border) */
    divider: boolean;
};

/** The settings in the order they are shown, with the consecutive ones of the same pair together (a setting without a pair is a group of its own) */
export function groupSettingsByPair(settings: SettingDefinition[]): SettingsGroup[] {
    const groups: SettingsGroup[] = [];

    for (const setting of settings) {
        const last = groups[groups.length - 1];

        if (setting.pair && last?.settings[0].pair === setting.pair) {
            last.settings.push(setting);
        } else {
            groups.push({ settings: [setting], divider: false });
        }
    }

    return groups.map((group, index) => ({ ...group, divider: group.settings.length === 1 && groups[index + 1]?.settings.length === 1 }));
}

/** Cards of a subcategory with only the settings that are currently visible; the cards left empty are dropped. */
export function buildSubcategoryCards(subcategory: SettingsSubcategory, context: SettingsContext): SettingsViewCard[] {
    return subcategory.cards
        .map(card => ({ id: card.id, title: card.title, fullWidth: card.fullWidth, settings: card.settings.filter(setting => isSettingVisible(setting, context)) }))
        .filter(card => card.settings.length > 0)
        .map(card => ({ ...card, groups: groupSettingsByPair(card.settings) }));
}

/** One card for each section (card of the registry) with settings found, in the order of the registry. */
export function buildSearchCards(results: SettingsSearchResult[], context: SettingsContext): SettingsViewCard[] {
    const cards = new Map<string, SettingsViewCard>();

    for (const { category, subcategory, card, setting } of results) {
        if (!isSubcategoryVisible(subcategory, context) || !isSettingVisible(setting, context)) {
            continue;
        }

        const id = `${subcategory.id}/${card.id}`;
        let viewCard = cards.get(id);

        if (!viewCard) {
            viewCard = { id, title: card.title, fullWidth: card.fullWidth, settings: [], groups: [], origin: { subcategoryId: subcategory.id, subcategoryTitle: subcategory.title, categoryTitle: category.title } };
            cards.set(id, viewCard);
        }

        viewCard.settings.push(setting);
    }

    return [...cards.values()].map(card => ({ ...card, groups: groupSettingsByPair(card.settings) }));
}
